import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiLinks } from '@cutting/app.api-links';
import {
  ChatAttachment,
  ChatMessage,
  ChatMessageResponse,
  CuttingOrderQuestion,
  Download,
  DownloadService,
  SupportChatService,
} from 'cloud-shared-lib';
import { BehaviorSubject, concatMap, Observable, Subject, tap } from 'rxjs';

@Injectable()
export class OrderQuestionChatService implements SupportChatService {
  newMessagesSubject: Subject<ChatMessage> = new Subject();
  newAttachmentsSubject: Subject<ChatAttachment[]> = new Subject();
  onQuestionChanged = new BehaviorSubject<CuttingOrderQuestion>(null);
  orderId: number;

  constructor(private http: HttpClient, private downloadService: DownloadService) {}

  createQuestion(): Observable<CuttingOrderQuestion | null> {
    const httpParams = new HttpParams().set('orderId', this.orderId.toString());

    return this.http
      .post<CuttingOrderQuestion>(ApiLinks.newOrderQuestion(), null, { params: httpParams })
      .pipe(tap(question => this.onQuestionChanged.next(question)));
  }

  getQuestion(questionId: number): Observable<CuttingOrderQuestion> {
    return this.http.get<CuttingOrderQuestion>(ApiLinks.orderQuestion(questionId));
  }

  getMessages(questionId: number, all = false): Observable<ChatMessageResponse> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('all', all);
    return this.http.get<ChatMessageResponse>(ApiLinks.orderQuestionMessage(questionId), {
      params: httpParams,
    });
  }

  sendMessage(questionId: number | null, newMessage: ChatMessage): Observable<ChatMessage> {
    if (questionId === null) {
      return this.createQuestion().pipe(concatMap(question => this.sendMessage(question.id, newMessage)));
    }
    return this.http.post<ChatMessage>(ApiLinks.orderQuestionMessage(questionId), newMessage);
  }

  getAttachmentFile(questionId: number, messageId: number, attachmentId: number): Observable<Download> {
    return this.downloadService.download(
      `${ApiLinks.orderQuestionMessageFiles(questionId, messageId)}/${attachmentId}`,
      { filenameFromContentDisposition: true }
    );
  }

  uploadFiles(files: File[], questionId: number, messageId: number): Observable<ChatAttachment[]> {
    const formData = new FormData();
    files.forEach((f: File) => {
      formData.append('attachmentFiles', f, f.name);
    });

    return this.http.post<ChatAttachment[]>(ApiLinks.orderQuestionMessageFiles(questionId, messageId), formData);
  }

  markAsRead(questionId: number): Observable<void> {
    return this.http.put<void>(ApiLinks.cuttingOrderMessagesMarkAsRead(questionId), undefined);
  }

  getUnreadedMessagesCount(questionId: number): Observable<number> {
    return this.http.get<number>(ApiLinks.orderUnreadedMessagesCount(questionId));
  }
}
